import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { API, api, formatApiError } from "../../lib/api";
import { SERVICES } from "../../data/services";

const field = "w-full bg-transparent border-0 border-b border-[#0A192F]/25 px-0 py-3 text-[#0A192F] placeholder:text-[#6B7385] focus:outline-none focus:border-[#8A6A2B] transition-colors duration-300";
const label = "font-mono text-[11px] uppercase tracking-[0.22em] text-[#5A6578]";

export const EnquiryForm = ({ defaultService = "" }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: defaultService || SERVICES[0].title, message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`${API}/enquiries`, { ...form, email: form.email || null });
      setDone(true);
      toast.success("Enquiry received. We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="py-12" data-testid="enquiry-success">
        <CheckCircle2 className="h-10 w-10 text-[#8A6A2B]" />
        <h3 className="mt-5 font-display text-4xl text-[#0A192F]">Thank you, {form.name.split(" ")[0]}.</h3>
        <p className="mt-3 text-[#2C3545] max-w-md">Your enquiry about <em>{form.service}</em> has reached our desk. We'll respond on {form.phone} soon.</p>
        <button onClick={() => { setDone(false); setForm((f) => ({ ...f, message: "" })); }} className="mt-8 text-sm underline underline-offset-4 text-[#8A6A2B]" data-testid="enquiry-new-btn">Send another enquiry</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid gap-8 sm:grid-cols-2" data-testid="enquiry-form">
      <label className="block"><span className={label}>Full name *</span>
        <input required minLength={2} value={form.name} onChange={set("name")} className={field} placeholder="Your name" data-testid="enquiry-name-input" /></label>
      <label className="block"><span className={label}>Mobile *</span>
        <input required minLength={7} value={form.phone} onChange={set("phone")} className={field} placeholder="+91" inputMode="tel" data-testid="enquiry-phone-input" /></label>
      <label className="block"><span className={label}>Email</span>
        <input type="email" value={form.email} onChange={set("email")} className={field} placeholder="you@example.com" data-testid="enquiry-email-input" /></label>
      <label className="block"><span className={label}>Service *</span>
        <select value={form.service} onChange={set("service")} className={`${field} cursor-pointer`} data-testid="enquiry-service-select">
          {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
          <option value="General Enquiry">General Enquiry</option>
        </select></label>
      <label className="block sm:col-span-2"><span className={label}>How can we help? *</span>
        <textarea required minLength={5} rows={4} value={form.message} onChange={set("message")} className={`${field} resize-none`} placeholder="Briefly describe the property, land or legal matter" data-testid="enquiry-message-input" /></label>
      <div className="sm:col-span-2">
        <button type="submit" disabled={loading} data-testid="enquiry-submit-btn"
          className="group inline-flex items-center gap-4 rounded-full bg-[#0A192F] pl-8 pr-2 py-2 text-[#F4EEE3] transition-[background-color,transform] duration-300 hover:bg-[#13284A] active:scale-[0.98] disabled:opacity-60">
          <span className="text-sm tracking-wide">{loading ? "Sending…" : "Submit enquiry"}</span>
          <span className="grid h-11 w-11 place-items-center rounded-full bg-[#C5A059] text-[#070F1E] transition-transform duration-500 group-hover:rotate-[-45deg]">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          </span>
        </button>
      </div>
    </form>
  );
};
